import React from 'react';
import './ErrorBoundary.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // You can log to an error reporting service here
    // e.g., Sentry, LogRocket, etc.
  }

  handleReset = () => {
    // Reset error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    // Reload the page as a last resort
    window.location.reload();
  };

  render() {
    const { hasError, error, errorInfo, errorCount } = this.state;
    const { children, fallback, componentName } = this.props;

    if (hasError) {
      // Custom fallback UI if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className="error-boundary-container">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">⚠️</div>
            <h2 className="error-boundary-title">
              {componentName ? `${componentName} Error` : 'Something went wrong'}
            </h2>
            <p className="error-boundary-message">
              We encountered an unexpected error. Don't worry, your data is safe.
            </p>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && error && (
              <details className="error-boundary-details">
                <summary>Error Details (Development Only)</summary>
                <div className="error-boundary-stack">
                  <strong>Error:</strong> {error.toString()}
                  <br />
                  <strong>Component Stack:</strong>
                  <pre>{errorInfo?.componentStack}</pre>
                </div>
              </details>
            )}

            <div className="error-boundary-actions">
              <button
                onClick={this.handleReset}
                className="error-boundary-button error-boundary-button-primary"
              >
                Try Again
              </button>
              {errorCount > 2 && (
                <button
                  onClick={this.handleReload}
                  className="error-boundary-button error-boundary-button-secondary"
                >
                  Reload Page
                </button>
              )}
            </div>

            <p className="error-boundary-help">
              If the problem persists, please try refreshing the page or{' '}
              <a href="/" className="error-boundary-link">
                return to home
              </a>
              .
            </p>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
